/**
 * InvoiceSuccessModal Component
 * Belge (fatura/teklif/sipariş/irsaliye) kaydetme başarı modalı
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type InvoiceMode = 'invoice' | 'offer' | 'order' | 'waybill';
type DocumentSide = 'sales' | 'purchase';

interface InvoiceSuccessModalProps {
  open: boolean;
  onClose: () => void;
  invoiceNumber: string;
  isEditMode?: boolean;
  mode?: InvoiceMode;
  documentSide?: DocumentSide;
}

const DOC_LABELS: Record<InvoiceMode, { titleNoun: string; lowerNoun: string; numberLabel: string }> = {
  invoice: { titleNoun: 'Fatura', lowerNoun: 'fatura', numberLabel: 'Fatura No' },
  offer: { titleNoun: 'Teklif', lowerNoun: 'teklif', numberLabel: 'Teklif No' },
  order: { titleNoun: 'Sipariş', lowerNoun: 'sipariş', numberLabel: 'Sipariş No' },
  waybill: { titleNoun: 'İrsaliye', lowerNoun: 'irsaliye', numberLabel: 'İrsaliye No' },
};

const SIDE_PREFIXES: Record<DocumentSide, string> = {
  sales: 'satış',
  purchase: 'alış',
};

export const InvoiceSuccessModal: React.FC<InvoiceSuccessModalProps> = ({
  open,
  onClose,
  invoiceNumber,
  isEditMode = false,
  mode = 'invoice',
  documentSide = 'sales',
}) => {
  const { titleNoun, lowerNoun, numberLabel } = DOC_LABELS[mode];

  // İrsaliye için satış/alış eki kullanılmaz
  const detailNoun =
    mode === 'waybill'
      ? lowerNoun
      : `${SIDE_PREFIXES[documentSide]} ${lowerNoun}`;

  const title = isEditMode ? `${titleNoun} Güncellendi` : `${titleNoun} Kaydedildi`;
  const headline = isEditMode
    ? `${titleNoun} başarıyla güncellendi!`
    : `${titleNoun} başarıyla kaydedildi!`;
  const detail = isEditMode
    ? `${invoiceNumber} numaralı ${detailNoun} başarıyla güncellendi.`
    : `${invoiceNumber} numaralı ${detailNoun} başarıyla oluşturuldu.`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{headline}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {detail}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {numberLabel}: <strong>{invoiceNumber}</strong>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Tamam
        </Button>
      </DialogActions>
    </Dialog>
  );
};
